import * as THREE from 'three';
import { gsap } from 'gsap';
import DeskScene from './DeskScene';
import ScreenManager from './ScreenManager';
import InteractionManager from './InteractionManager';
import CameraManager from './CameraManager';
import LightingManager from './LightingManager';

export default class SceneManager {
  constructor(container) {
    this.container = container;
    this.scene = new THREE.Scene();
    this.setupScene();
  }

  setupScene() {
    // Detect mobile devices for performance optimization
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Setup renderer with mobile optimizations
    this.renderer = new THREE.WebGLRenderer({ 
      antialias: !this.isMobile, // Disable antialiasing on mobile for performance
      powerPreference: this.isMobile ? "low-power" : "high-performance"
    });
    
    const width = this.container.clientWidth || window.innerWidth;
    const height = this.container.clientHeight || window.innerHeight;
    this.renderer.setSize(width, height);
    
    // Reduce pixel ratio on mobile for better performance
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.isMobile ? 1 : 2));
    
    // Disable expensive effects on mobile
    this.renderer.shadowMap.enabled = !this.isMobile;
    if (!this.isMobile) {
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    }
    
    this.container.appendChild(this.renderer.domElement);

    // Initialize managers with mobile optimizations
    this.cameraManager = new CameraManager(this.container, this.renderer);
    this.lightingManager = new LightingManager(this.scene, this.isMobile);
    this.deskScene = new DeskScene(this.scene, this.isMobile);
    this.screenManager = new ScreenManager(this.scene, this.isMobile);
    this.interactionManager = new InteractionManager(
      this.scene,
      this.cameraManager,
      this.deskScene,
      this.screenManager,
      this.renderer.domElement,
      this.container,
      this.isMobile
    );

    // Setup interaction
    this.interactionManager.setupMouseInteraction(this.container);

    // Handle window resize
    window.addEventListener('resize', this.onWindowResize.bind(this));
  }

  onWindowResize() {
    this.cameraManager.onResize();
    const width = this.container.clientWidth || window.innerWidth;
    const height = this.container.clientHeight || window.innerHeight;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.isMobile ? 1 : 2));
    this.renderer.setSize(width, height);
  }

  async showProjectOnScreen(project) {
    await this.screenManager.showProjectOnScreen(project);
  }

  transitionToSection(section) {
    this.cameraManager.transitionToSection(section);
  }

  toggleDayNightMode() {
    this.lightingManager.toggleDayNightMode();
  }

  update() {
    // Update all managers
    this.cameraManager.update();
    this.interactionManager.update();
    this.deskScene.update();
    this.screenManager.update();

    // Render the scene
    this.renderer.render(this.scene, this.cameraManager.getCamera());
  }
} 